'use client';

import Link from 'next/link';
import { Clock, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/utils/formatting';
import type { BlogPost } from '@/lib/api/content';
import { getBlogPostImageUrl } from '@/lib/api/content';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';

interface BlogPostCardProps {
  post: BlogPost;
  className?: string;
  showSummary?: boolean;
  compact?: boolean;
}

export function BlogPostCard({
  post,
  className,
  showSummary = true,
  compact = false,
}: BlogPostCardProps) {
  const imageUrl = getBlogPostImageUrl(post);
  const publishedDate = formatDate(post.publishedAt, 'medium');
  const readingTime = post.readingTimeMinutes ?? 1;

  return (
    <Card
      variant="glass"
      className={cn(
        'group flex flex-col overflow-hidden border-neon-cyan/10 transition-colors hover:border-neon-cyan/25',
        className
      )}
    >
      {imageUrl && !compact && (
        <Link href={`/blog/${post.slug}`} className="block overflow-hidden">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt={post.title}
            className="aspect-[16/9] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
          />
        </Link>
      )}

      <div className="flex flex-1 flex-col p-5">
        {post.category && (
          <Badge variant="info" className="mb-3 w-fit">
            <Link
              href={`/blog?category=${encodeURIComponent(post.category.slug)}`}
              className="inline-flex min-h-11 items-center hover:underline"
            >
              {post.category.name}
            </Link>
          </Badge>
        )}

        <h3 className="font-semibold text-foreground transition-colors group-hover:text-neon-cyan">
          <Link href={`/blog/${post.slug}`} className="inline-flex min-h-11 items-center">{post.title}</Link>
        </h3>

        {showSummary && post.summary && (
          <p className="mt-2 line-clamp-3 text-sm text-ash">{post.summary}</p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-3 pt-4 text-xs text-muted">
          {publishedDate && <time dateTime={post.publishedAt}>{publishedDate}</time>}
          {post.readingTimeMinutes !== null && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {readingTime} min read
            </span>
          )}
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            {post.viewCount.toLocaleString()} views
          </span>
        </div>
      </div>
    </Card>
  );
}

export default BlogPostCard;
